// src/merch/store.js
//
// MerchMod design state. Layers use the SAME shape as Tattune layers
// ({ id, name, url, visible, opacity, blend, transform, kind, text }) so
// the shared engine composites and exports them unchanged.
//
// Layer order: index 0 = TOP (matches Tattune / engine expectations).
//
// Default blend is "block" (normal paint): a printed sticker is opaque
// artwork, unlike Tattune's ink-on-skin "skin" multiply.

import { proxy } from "valtio";
import { renderTextLayer, TEXT_DEFAULTS, downscaleImageFile } from "../engine";
import { PRODUCTS, getProduct, getSize, getColour, getSides } from "./products";

const first = PRODUCTS[0];
const firstZone = (p, sz) => getSides(p, sz)[0].id;

export const merch = proxy({
  productId: first.id,
  sizeId: first.sizes[0].id,
  colourId: getColour(first, null).id,
  hand: "right", // "left" | "right" — shot direction for handed products (sticks)
  // contour-cut products (die-cut sticker): cut line offset + one piece / separate
  contour: { offsetMm: 3.175, single: true },
  saved: null,   // { id, editToken } once saved to the cloud (Save project)
  dragging: false, // a layer is being moved in the editor (pauses re-compositing)
  loaded: false,   // autosave / saved design restored (safe to add defaults)
  // `layers` is always the ACTIVE side's design; other sides are parked in
  // `sideLayers` (see setSide). Keeps every layer tool side-agnostic.
  side: firstZone(first, first.sizes[0]),
  sideLayers: {},
  sideRev: 0,       // bumps when a parked side changes (for its composite)
  layers: [],
  selectedId: null,
  rev: 0,
  // freehand drawing (see paint.js)
  paint: {
    active: false,
    color: "#111111",
    sizeMm: 1.2,      // true pen width on the printed sticker
    smooth: true,     // rope stabilizer on/off
    opacity: 100,     // % for soft / marker / spray
    smoothLevel: 5,   // 1–10: string length (steadier ↔ more responsive)
    brush: "pen",     // pen | marker | soft | spray
    tool: "brush",    // brush | fill (paint bucket)
    erase: false,
    strokes: 0,       // count, for enabling Undo/Clear/Add buttons
    rev: 0,           // bumps when the paint buffer changes
  },
});

// New layers go on top — but under anything pinned to the top (the jersey
// hair, which hangs over names / numbers until the user moves it).
function insertTop(layers, L) {
  let i = 0;
  while (i < layers.length && layers[i].pinTop) i++;
  return [...layers.slice(0, i), L, ...layers.slice(i)];
}

let idSeq = 0;
const newId = () => `m${Date.now().toString(36)}${(idSeq++).toString(36)}`;
const bump = () => { merch.rev++; };

function baseLayer(partial) {
  return {
    id: newId(),
    name: "Layer",
    url: "",
    visible: true,
    opacity: 1,
    blend: "block",
    edgeClip: 0.05,
    transform: { tx: 0.5, ty: 0.5, scale: 0.5, rot: 0 },
    ...partial,
  };
}

/* ---------- product ---------- */

export function setProduct(id) {
  const p = getProduct(id);
  setSide(firstZone(p, p.sizes[0]));
  merch.productId = p.id;
  merch.sizeId = p.sizes[0].id;
  merch.colourId = getColour(p, merch.colourId).id;
  bump();
}
export function setSize(id) {
  merch.sizeId = getSize(getProduct(merch.productId), id).id;
  bump();
}
export function setHand(hand) {
  merch.hand = hand === "left" ? "left" : "right";
  merch.sideRev++; // parked zones re-render for the mirrored 3D model
  bump();
}
export function setColour(id) {
  merch.colourId = getColour(getProduct(merch.productId), id).id;
  bump();
}

/* ---------- layers ---------- */

function readAsDataURL(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export async function addImageFile(file) {
  if (!file) return;
  let prepared = file;
  try {
    prepared = await downscaleImageFile(file, 2048);
  } catch {
    /* fall back to original */
  }
  const url = await readAsDataURL(prepared);
  const L = baseLayer({
    name: (file.name || "Image").replace(/\.[^.]+$/, "").slice(0, 32),
    url,
    kind: "upload", // customer's own image — flagged for review (logos/copyright)
    transform: { tx: 0.5, ty: 0.5, scale: 0.3, rot: 0 },
  });
  merch.layers = insertTop(merch.layers, L);
  merch.selectedId = L.id;
  bump();
}

export async function addText(settings = {}) {
  const text = { ...TEXT_DEFAULTS, ...settings };
  const { url, refEdge } = await renderTextLayer(text);
  const L = baseLayer({
    name: (text.text || "Text").split(/\r?\n/)[0].slice(0, 32) || "Text",
    url,
    kind: "text",
    text,
    refEdge,
    transform: { tx: 0.5, ty: 0.5, scale: 0.35, rot: 0 },
  });
  merch.layers = insertTop(merch.layers, L);
  merch.selectedId = L.id;
  bump();
}

export async function updateText(id, patch = {}) {
  const L = merch.layers.find((l) => l.id === id);
  if (!L || L.kind !== "text") return;
  const text = { ...TEXT_DEFAULTS, ...(L.text || {}), ...patch };
  const { url, refEdge } = await renderTextLayer(text);
  // keep the LETTERS the same size when the image's shape changes (outline,
  // spacing, more letters…): the layer box grows/shrinks instead
  const L2 = merch.layers.find((l) => l.id === id) || L; // may have moved while rendering
  if (L2.refEdge && refEdge && Math.abs(refEdge - L2.refEdge) > 0.5) {
    const f = refEdge / L2.refEdge;
    const t = L2.transform || {};
    const clamp = (x) => Math.min(3, Math.max(0.02, x));
    const next = { ...t, scale: clamp((t.scale || 1) * f) };
    if (typeof t.scaleX === "number") next.scaleX = clamp(t.scaleX * f);
    if (typeof t.scaleY === "number") next.scaleY = clamp(t.scaleY * f);
    L2.transform = next;
  }
  L2.refEdge = refEdge;
  L2.url = url;
  L2.text = text;
  L2.name = (text.text || "Text").split(/\r?\n/)[0].slice(0, 32) || "Text";
  bump();
}

/** Add a ready-made image layer (used by "Add as layer" from paint). */
export function addLayerRaw(partial, { bottom = false } = {}) {
  const L = baseLayer(partial);
  merch.layers = bottom ? [...merch.layers, L] : insertTop(merch.layers, L);
  merch.selectedId = L.id;
  bump();
  return L.id;
}

/* ---------- sides (front / back) ---------- */

export function layersForSide(side) {
  return side === merch.side ? merch.layers : merch.sideLayers[side] || [];
}

/** Switch the active side. Call commitPaintAsLayer() first (see sides.js). */
export function setSide(side) {
  if (side === merch.side) return;
  merch.sideLayers = { ...merch.sideLayers, [merch.side]: merch.layers };
  merch.layers = merch.sideLayers[side] || [];
  merch.side = side;
  merch.selectedId = merch.layers[0]?.id || null;
  merch.sideRev++;
  bump();
}

/** Mirror position/rotation about the sticker's vertical centre line.
 *  Artwork flips; text (and drawings — usually signatures) stays readable. */
export function mirrorLayer(L) {
  const t = L.transform || {};
  const keepReadable = L.kind === "text" || L.kind === "paint";
  return {
    ...L,
    transform: {
      ...t,
      tx: 1 - (t.tx ?? 0.5), // print areas are centred on 0.5
      rot: -(t.rot || 0),
      flipX: keepReadable ? !!t.flipX : !t.flipX,
    },
  };
}

// Jersey art is drawn per side (front ≠ back), so it's never copied or
// replaced by side/template tools — each side keeps its own.
const isJ = (L) => L?.kind === "jersey" || L?.kind === "jersey-helmet" || L?.kind === "jersey-mask" || L?.kind === "jersey-hair";
const keepJersey = (next, prev) => {
  const J = (prev || []).find((l) => l.kind === "jersey");
  const H = (prev || []).find((l) => l.kind === "jersey-helmet");
  const M = (prev || []).find((l) => l.kind === "jersey-mask");
  const HR = (prev || []).find((l) => l.kind === "jersey-hair");
  if (!J && !H && !M && !HR) return next;
  const faces = next.filter((l) => l.kind === "face");
  const others = next.filter((l) => l.kind !== "face" && !isJ(l));
  return [...(HR ? [HR] : []), ...others, ...(M ? [M] : []), ...(H ? [H] : []), ...faces, ...(J ? [J] : [])];
};

/** Replace side `to` with a copy of side `from` (optionally mirrored). */
export function copySide(from, to, { mirror = false } = {}) {
  const src = layersForSide(from).filter((L) => !isJ(L));
  let copies = src.map((L) => {
    const c = { ...L, id: newId(), transform: { ...L.transform }, text: L.text ? { ...L.text } : L.text };
    return mirror ? mirrorLayer(c) : c;
  });
  copies = keepJersey(copies, layersForSide(to));
  if (to === merch.side) {
    merch.layers = copies;
    merch.selectedId = copies.find((l) => !isJ(l))?.id || null;
  } else {
    merch.sideLayers = { ...merch.sideLayers, [to]: copies };
    merch.sideRev++;
  }
  bump();
}

/** Toggle a single layer's horizontal flip. */
export function toggleFlip(id) {
  const L = merch.layers.find((l) => l.id === id);
  if (!L) return;
  const flip = !L.transform?.flipX;
  const cx = L.transform?.tx ?? 0.5;
  for (const M of linkedWith(id)) {
    const m = M.transform || {};
    // mirror linked layers across this layer's centre too
    M.transform = M === L ? { ...m, flipX: flip } : { ...m, flipX: flip, tx: 2 * cx - (m.tx ?? 0.5) };
  }
  bump();
}

/** Set several zones at once (whole-stick templates). `map` = { zoneId:
 *  [partial layers] }; zones not in the map are left alone. */
export function setZoneLayers(map) {
  const parked = { ...merch.sideLayers };
  for (const [id, partials] of Object.entries(map)) {
    let layers = partials.map((p) => baseLayer({ ...p, id: undefined }));
    for (const L of layers) if (!L.id) L.id = newId();
    layers = keepJersey(layers, id === merch.side ? merch.layers : merch.sideLayers[id]);
    if (id === merch.side) {
      merch.layers = layers;
      merch.selectedId = (layers.find((l) => l.kind === "text") || layers[0])?.id || null;
    } else parked[id] = layers;
  }
  merch.sideLayers = parked;
  merch.sideRev++;
  bump();
}

/** Replace the design with template layers (from templates.js). */
export function replaceLayers(partials) {
  const layers = keepJersey(partials.map((p) => baseLayer(p)), merch.layers);
  merch.layers = layers;
  // select the name (or first text) so kids can edit straight away
  merch.selectedId = (layers.find((l) => l.kind === "text") || layers[0])?.id || null;
  bump();
}

export function getLayer(id) {
  return merch.layers.find((l) => l.id === id) || null;
}

export function selectLayer(id) {
  merch.selectedId = id;
}

export function updateTransform(id, patch) {
  const L = merch.layers.find((l) => l.id === id);
  if (!L) return;
  const t0 = L.transform || {};
  const t1 = { ...t0, ...patch };
  L.transform = t1;
  // linked layers follow: same move, and the same turn / resize about
  // this layer's centre (so stacked lettering stays stacked)
  if (L.link) {
    const cx0 = t0.tx ?? 0.5, cy0 = t0.ty ?? 0.5, cx1 = t1.tx ?? 0.5, cy1 = t1.ty ?? 0.5;
    const f = (t1.scale || 1) / (t0.scale || 1);
    const dr = (t1.rot || 0) - (t0.rot || 0);
    const c = Math.cos(dr), sn = Math.sin(dr);
    for (const M of merch.layers) {
      if (M === L || M.link !== L.link || M.locked) continue;
      const m = M.transform || {};
      const ox = ((m.tx ?? 0.5) - cx0) * f, oy = ((m.ty ?? 0.5) - cy0) * f;
      const next = { ...m, tx: cx1 + ox * c - oy * sn, ty: cy1 + ox * sn + oy * c, rot: (m.rot || 0) + dr };
      if (f !== 1) {
        next.scale = (m.scale || 1) * f;
        if (typeof m.scaleX === "number") next.scaleX = m.scaleX * f;
        if (typeof m.scaleY === "number") next.scaleY = m.scaleY * f;
      }
      M.transform = next;
    }
  }
  bump();
}

/** Layers in the same link group as `id` (including it), top first. */
export function linkedWith(id, layers = merch.layers) {
  const L = layers.find((l) => l.id === id);
  if (!L?.link) return L ? [L] : [];
  return layers.filter((l) => l.link === L.link);
}

/** Link a layer with the one below it (they then move, turn and resize
 *  together), or unlink it if it's already linked. */
export function toggleLink(id) {
  const arr = merch.layers;
  const i = arr.findIndex((l) => l.id === id);
  const L = arr[i];
  if (!L || L.locked) return;
  if (L.link) {
    const g = L.link;
    L.link = undefined;
    const rest = arr.filter((l) => l.link === g);
    if (rest.length === 1) rest[0].link = undefined; // a group of one isn't a group
  } else {
    const B = arr[i + 1];
    if (!B || B.locked) return;
    const g = B.link || newId();
    L.link = g;
    B.link = g;
  }
  bump();
}

/** Start a fresh design: every side emptied, unlinked from any saved
 *  project (so Save makes a new one). Product, size and colour stay. */
export function startOver() {
  merch.layers = [];
  merch.sideLayers = {};
  merch.selectedId = null;
  merch.saved = null;
  merch.sideRev++;
  bump();
}

/** Show / hide a layer (hidden layers don't print). */
export function toggleVisible(id) {
  const L = merch.layers.find((l) => l.id === id);
  if (!L) return;
  L.visible = !(L.visible ?? true);
  bump();
}

/** Blend: "block" (normal), "ink" (multiply: white vanishes), "whitefill"
 *  (solid white silhouette — outlines / backing on wood or black). */
export function setBlend(id, blend) {
  const L = merch.layers.find((l) => l.id === id);
  if (!L || L.locked) return;
  L.blend = blend;
  bump();
}

/** Copy a layer directly above itself, same position. */
export function duplicateLayer(id) {
  const i = merch.layers.findIndex((l) => l.id === id);
  const L = merch.layers[i];
  if (!L || L.locked) return null;
  const t = L.transform || {};
  const copy = {
    ...L,
    id: newId(),
    name: `${L.name} copy`.slice(0, 40),
    text: L.text ? { ...L.text } : L.text,
    transform: { ...t }, // exactly on top: stacked lettering, outlines
    link: undefined,     // the copy starts unlinked (link it with 🔗)
  };
  const arr = [...merch.layers];
  arr.splice(i, 0, copy); // index 0 = top, so this sits directly above
  merch.layers = arr;
  merch.selectedId = copy.id;
  bump();
  return copy.id;
}

/** Layer opacity 0–1. */
export function setOpacity(id, value) {
  const L = merch.layers.find((l) => l.id === id);
  if (!L) return;
  L.opacity = Math.max(0, Math.min(1, value));
  bump();
}

export function removeLayer(id) {
  merch.layers = merch.layers.filter((l) => l.id !== id);
  if (merch.selectedId === id) merch.selectedId = merch.layers[0]?.id || null;
  bump();
}

export function moveLayer(id, dir) {
  const arr = [...merch.layers];
  const i = arr.findIndex((l) => l.id === id);
  const j = dir === "up" ? i - 1 : i + 1; // index 0 = top
  if (i < 0 || j < 0 || j >= arr.length) return;
  [arr[i], arr[j]] = [arr[j], arr[i]];
  // moving a pinned layer (or moving something above it) unpins it: the
  // user's order wins from now on
  for (const L of [arr[i], arr[j]]) if (L.pinTop) arr[arr.indexOf(L)] = { ...L, pinTop: false };
  merch.layers = arr;
  bump();
}
